import React from "react";
import { motion } from "framer-motion";
import { BookOpen, Sunrise, MoonStar, CalendarCheck, Clock } from "lucide-react";
import { useGoTo } from "../nav";
import { useLang } from "../i18n";
import { WaveDivider } from "./decor";

const CARDS = [
  { id: "weekday-care", color: "bg-cardBlue", icon: BookOpen },
  { id: "early-drop-off", color: "bg-cardPeach", icon: Sunrise },
  { id: "late-pick-up", color: "bg-cardMauve", icon: MoonStar },
  { id: "saturday-care", color: "bg-cardTan", icon: CalendarCheck },
];

export default function Programs() {
  const goTo = useGoTo();
  const { s } = useLang();
  return (
    <section id="programs" data-testid="programs-section" className="relative bg-coral pt-10 pb-16 overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="font-display font-bold text-center text-white text-4xl sm:text-5xl lg:text-6xl"
        >
          {s.programs.h}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-4 text-white/90 text-base sm:text-lg leading-relaxed text-center max-w-2xl mx-auto"
        >
          {s.flex.desc}
        </motion.p>

        {/* regular hours strip */}
        <motion.div
          data-testid="regular-hours-card"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 max-w-2xl mx-auto rounded-full bg-white shadow-card px-6 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 text-center"
        >
          <span className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-coral" />
            <span className="font-display font-semibold text-forest text-sm sm:text-base">{s.flex.hoursLabel}:</span>
          </span>
          <span className="font-display font-bold text-forest text-base sm:text-lg">
            {s.flex.days} · {s.flex.hours}
          </span>
        </motion.div>

        <div className="mt-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-24">
          {s.programs.items.map((p, i) => {
            const Card = CARDS[i];
            return (
              <motion.div
                key={Card.id}
                data-testid={`program-card-${Card.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`relative ${Card.color} rounded-3xl px-6 pt-20 pb-8 text-center shadow-card hover:-translate-y-1.5 transition-transform duration-300`}
              >
                <span className="absolute -top-14 left-1/2 -translate-x-1/2 w-28 h-28 rounded-full bg-white ring-8 ring-white/50 flex items-center justify-center">
                  <Card.icon className="w-11 h-11 text-forest" strokeWidth={1.8} />
                </span>
                <h3 className="font-display font-semibold text-forest uppercase tracking-wide text-lg">
                  {p.name}
                </h3>
                <p className="text-forest/80 text-[15px] leading-relaxed mt-3">{p.desc}</p>
                <p className="font-display font-bold text-forest text-[15px] mt-4">{p.meta}</p>
              </motion.div>
            );
          })}
        </div>

        {/* overnight care banner */}
        <motion.div
          data-testid="program-card-overnight-care"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="mt-16 bg-pLavender rounded-3xl p-7 sm:p-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left shadow-card"
        >
          <span className="w-20 h-20 shrink-0 rounded-full bg-white ring-8 ring-white/50 flex items-center justify-center">
            <MoonStar className="w-9 h-9 text-forest" strokeWidth={1.8} />
          </span>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-forest uppercase tracking-wide text-xl">
              {s.programs.overnight.name}
            </h3>
            <p className="text-forest/80 text-[15px] leading-relaxed mt-2">{s.programs.overnight.desc}</p>
            <p className="font-display font-bold text-forest text-[15px] mt-2">{s.programs.overnight.meta}</p>
          </div>
        </motion.div>

        <div className="text-center mt-14">
          <a
            data-testid="programs-enroll-button"
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              goTo("/contact");
            }}
            className="inline-flex rounded-full bg-brown text-white font-display font-semibold tracking-wide uppercase text-base px-10 py-4 shadow-card hover:bg-[#4a338f] hover:scale-[1.03] transition-all duration-200"
          >
            {s.programs.enroll}
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0">
        <WaveDivider fill="#FAF8F1" />
      </div>
    </section>
  );
}
